import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/query/react'
import { ApiResponseSerializer } from '@v2/commons/interface/ApiResponseSerializer'

import { rootApi } from 'src/v2/store/api/rootApi'

import {
  type ITag,
  type TagCreateMutationData,
  type TagQueryParams,
  type TagUpdateMutationData,
} from '../../tag/tag.types'

export const tagApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getTags: builder.query<
      ApiResponseSerializer<ITag>,
      { params: TagQueryParams; organizationId?: string }
    >({
      query: (queryArgs) => ({
        url: '/tags',
        method: 'GET',
        params: queryArgs.params,
      }),
      providesTags: (result, error, arg) =>
        result && arg.params.search
          ? [
              ...result.records.map(({ id }) => ({ type: 'Tag' as const, id })),
              'Tag',
            ]
          : [],
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        const { offset, ...rest } = queryArgs.params
        return defaultSerializeQueryArgs({
          endpointName,
          endpointDefinition,
          queryArgs: { ...rest, organizationId: queryArgs.organizationId },
        })
      },
      merge: (currentCache, newItems, { arg }) => {
        if (
          arg.params.offset === 0 ||
          !!arg.params.search ||
          (arg.params.search === '' && arg.params.offset === 0)
        ) {
          return newItems
        }
        return {
          ...newItems,
          records: [...(currentCache?.records || []), ...newItems.records],
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          (!!currentArg?.params.offset &&
            currentArg?.params.offset !== previousArg?.params.offset) ||
          currentArg?.params.search === ''
        )
      },
    }),
    updateTag: builder.mutation<
      ITag,
      {
        data: TagUpdateMutationData
      }
    >({
      query: ({ data }) => {
        return {
          url: `/tags`,
          method: 'PATCH',
          data,
        }
      },
      invalidatesTags: (_, _1, arg) => {
        return [{ type: 'Tag', id: arg.data.id }]
      },
    }),
    createTag: builder.mutation<
      ITag,
      {
        data: TagCreateMutationData
      }
    >({
      query: ({ data }) => {
        return {
          url: `/tags`,
          method: 'POST',
          data,
        }
      },
    }),
    deleteTag: builder.mutation<{ ok: boolean }, TagUpdateMutationData['id']>({
      query: (tagId) => ({
        url: `/tags/${tagId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, _1, arg) => [{ type: 'Tag', id: arg }],
    }),
  }),
})

export const {
  useGetTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
} = tagApi
